import type { DeliverableView, ReferenceOption } from "@fieldpath/shared";
import { useEffect, useRef, useState } from "react";

import { api, ApiError, type Fetcher } from "../api/client";
import { navigate } from "../router";
import { DateHint, PAST_DATE_BOX } from "./DateHint";
import { PeoplePicker } from "./PeoplePicker";
import { SearchSelect } from "./SearchSelect";

/**
 * Creating a task.
 *
 * Its own file because two screens raise tasks: the project, where the
 * deliverable is a choice, and a deliverable itself, where it is already
 * decided. `fixedDeliverableId` is the difference — when it is set the task
 * is created inside that deliverable and the picker is not offered, because
 * being asked to choose the thing you just clicked "Add task" from is the
 * sort of question that makes software feel like paperwork.
 */
export function NewTask({
  fetcher,
  projectId,
  projectPmKey,
  deliverables,
  fixedDeliverableId,
  onCreated,
}: {
  fetcher: Fetcher;
  projectId: string;
  /** The project's PM -- the owner default when the deliverable has none (E9.2). */
  projectPmKey?: string | null;
  deliverables: DeliverableView[];
  /** When set, the task belongs to this deliverable and is not asked about. */
  fixedDeliverableId?: string;
  onCreated: () => void;
}) {
  const [deliverableId, setDeliverableId] = useState(fixedDeliverableId ?? "");
  const [figureTypeId, setFigureTypeId] = useState("");
  const [figureTypes, setFigureTypes] = useState<{ id: string; name: string }[]>([]);

  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [ownerKey, setOwnerKey] = useState("");
  // Tracks whether the person has chosen an owner themselves. Without it the
  // default would overwrite a deliberate choice the moment the deliverable
  // selection changed.
  const [ownerTouched, setOwnerTouched] = useState(false);
  const [workflowId, setWorkflowId] = useState("");
  const [managers, setManagers] = useState<ReferenceOption[]>([]);
  const [people, setPeople] = useState<ReferenceOption[]>([]);
  const [workflows, setWorkflows] = useState<
    { id: string; name: string; requiresFigureType: boolean }[]
  >([]);
  const [roles, setRoles] = useState<
    { id: string; name: string; stages: { name: string; first: boolean }[] }[]
  >([]);
  // A REQUEST (F140) comes from somebody with neither the PM flag nor the
  // admin flag, and must put somebody on its first stage (F175, Jeremy,
  // 1 Nov 2026). Unknown is null, which asks nothing -- the server decides.
  const [requester, setRequester] = useState<boolean | null>(null);
  const [roleAssignments, setRoleAssignments] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // The refusal is at the top of the form and Create task at the bottom: on a
  // workflow with several roles the message would land out of view, which
  // reads as the button doing nothing (F175).
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [error]);

  useEffect(() => {
    void api.projectManagers(fetcher).then(setManagers).catch(() => undefined);
    void api.employees(fetcher).then(setPeople).catch(() => undefined);
    void api.listWorkflows(fetcher).then(setWorkflows).catch(() => undefined);
    void api.figureTypes(fetcher).then(setFigureTypes).catch(() => undefined);
    void Promise.all([api.me(fetcher), api.projectManagers(fetcher), api.amIAdmin(fetcher)])
      .then(([me, pms, admin]) => {
        const key = me.employee?.ajeraKey ?? null;
        setRequester(key === null ? null : !admin && !pms.some((p) => p.key === key));
      })
      .catch(() => setRequester(null));
  }, [fetcher]);

  // The roles belong to the chosen workflow, so they only make sense once one
  // is picked — and picking a different workflow invalidates the answers.
  useEffect(() => {
    setRoleAssignments({});
    if (!workflowId) {
      setRoles([]);
      return;
    }
    void api
      .workflowRoles(fetcher, workflowId)
      .then(setRoles)
      .catch(() => setRoles([]));
  }, [fetcher, workflowId]);

  // Nine times in ten the deliverable's owner owns every task in it, so it
  // is filled in for you — and left editable for the tenth. With no
  // deliverable, or one with no owner, the PROJECT's PM is next (E9.2).
  const effectiveDeliverable = fixedDeliverableId ?? deliverableId;
  const fromDeliverable =
    deliverables.find((x) => x.id === effectiveDeliverable)?.ownerKey ?? null;
  const ownerSource = fromDeliverable ? "deliverable" : projectPmKey ? "project" : null;
  useEffect(() => {
    if (ownerTouched) return;
    setOwnerKey(fromDeliverable ?? projectPmKey ?? "");
  }, [fromDeliverable, projectPmKey, ownerTouched]);

  // The role holding the workflow's first stage, and that stage's name.
  const firstRole = roles.find((r) => r.stages.some((s) => s.first)) ?? null;
  const firstStageName = firstRole?.stages.find((s) => s.first)?.name ?? null;
  const firstStageRequired = requester === true && firstRole !== null;

  const needsFigureType =
    workflows.find((w) => w.id === workflowId)?.requiresFigureType ?? false;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    // Said here as well as by the server, so nothing is created first.
    if (firstStageRequired && (roleAssignments[firstRole!.id] ?? []).length === 0) {
      setError(`Put someone on ${firstStageName}, the first stage, before creating this request.`);
      return;
    }
    setSaving(true);
    try {
      const created = await api.createTask(fetcher, projectId, {
        title,
        // Filed AS IT IS CREATED, in the same insert. Moving a task between
        // deliverables afterwards is limited to the people accountable for
        // it, and grouping in a second save used to need an exception for
        // whoever created it (security review, 12 Oct).
        deliverableId: effectiveDeliverable || null,
        figureTypeId: figureTypeId || null,
        dueDate: dueDate || null,
        // LEFT OUT when blank and untouched, so the server applies the
        // project PM (E9.2) -- which covers the deliverable screen, where this
        // form does not know the project. Choosing "Unassigned" on purpose
        // sends null, and null is kept.
        ...(ownerKey
          ? { ownerKey: ownerKey as never }
          : ownerTouched
            ? { ownerKey: null }
            : {}),
        workflowId: workflowId || null,
        roleAssignments,
      });
      onCreated();
      // Straight to the task just made (punch list #26, Jeremy 16 Oct 2026):
      // the next thing anybody does is open it to plan the stages, and the
      // list it was made from left them hunting for it.
      navigate({ kind: "task", id: created.id });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="card" onSubmit={submit}>
      <h2>New task</h2>
      {error ? (
        <div className="error" ref={errorRef}>
          {error}
        </div>
      ) : null}

      <div className="field">
        <label htmlFor="t-title">Title</label>
        <input id="t-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
      </div>

      {fixedDeliverableId ? null : deliverables.length > 0 ? (
        <div className="field">
          <label htmlFor="t-deliv">Part of — optional</label>
          <select
            id="t-deliv"
            value={deliverableId}
            onChange={(e) => setDeliverableId(e.target.value)}
          >
            <option value="">Not part of a deliverable</option>
            {deliverables.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div className="field">
        {/* The task's own date; the client date is the deliverable's
            (punch list #41). */}
        <label htmlFor="t-due">Task final due date</label>
        <input
          id="t-due"
          type="date"
          {...PAST_DATE_BOX}
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
      <DateHint />
      </div>

      {needsFigureType ? (
        <div className="field">
          <label htmlFor="t-fig">Figure type — required for GIS work</label>
          <select
            id="t-fig"
            value={figureTypeId}
            onChange={(e) => setFigureTypeId(e.target.value)}
            required
          >
            <option value="">Choose one…</option>
            {figureTypes.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {/* Every new task follows a workflow (punch list #56, Jeremy 20 Oct
          2026): "None — a simple task" is no longer offered. Tasks already
          made without one keep working - their status buttons included. */}
      <div className="field">
        <label htmlFor="t-wf">Workflow</label>
        <select id="t-wf" value={workflowId} onChange={(e) => setWorkflowId(e.target.value)} required>
          <option value="">Choose a workflow…</option>
          {workflows.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
      </div>

      {/* ALL the people in one place (punch list #49, 20 Oct 2026): the PM /
          Task Manager first, then whoever fills each of the workflow's roles.
          The PM / TM sat on its own near the top until then. */}
      <h2 style={{ marginTop: 20 }}>Who is doing what</h2>
      <div className="field">
        <label htmlFor="t-owner">
          PM / Task Manager — accountable end to end
          {!ownerTouched && ownerKey && ownerSource ? (
            <span className="muted"> · from the {ownerSource}</span>
          ) : null}
        </label>
        {/* Type to find, like every list of people (punch list #27). Clearing
            it with × is choosing "Unassigned" on purpose. */}
        <SearchSelect
          id="t-owner"
          options={managers}
          value={ownerKey}
          emptyLabel="Unassigned"
          onChange={(key) => {
            setOwnerTouched(true);
            setOwnerKey(key);
          }}
        />
      </div>
      {roles.length > 0 ? (
        <>
          <p className="muted">
            Each role is answered once here, and every stage that role holds is
            filled in. Any individual stage can be reassigned later.
          </p>
          {roles.map((role) => (
            <PeoplePicker
              key={role.id}
              label={
                firstStageRequired && role.id === firstRole!.id
                  ? `${role.name} — required — someone must be on the first stage`
                  : role.name
              }
              // Which stages this role holds, so the first one can be found
              // (F175): "Stages: Assignee Task (first), Assignee EDIT".
              hint={
                role.stages.length > 0
                  ? `Stages: ${role.stages.map((s) => (s.first ? `${s.name} (first)` : s.name)).join(", ")}`
                  : undefined
              }
              people={people}
              selected={roleAssignments[role.id] ?? []}
              onChange={(keys) =>
                setRoleAssignments((current) => ({ ...current, [role.id]: keys }))
              }
            />
          ))}
        </>
      ) : null}

      <button className="primary" type="submit" disabled={saving}>
        {saving ? "Creating…" : "Create task"}
      </button>
    </form>
  );
}
